const STRONG_BULLET = /(^|\s)[•●▪◦]\s*/gu;
const MIDDLE_DOT_SEPARATOR = /\s+·\s+/u;

const JOB_SECTION_HEADINGS = new Set([
  "about this role",
  "about the role",
  "about this job",
  "about the job",
  "about us",
  "about the company",
  "role overview",
  "role summary",
  "job overview",
  "job summary",
  "job description",
  "the role",
  "your role",
  "what you'll do",
  "what you’ll do",
  "what you will do",
  "what we're looking for",
  "what we’re looking for",
  "what we are looking for",
  "who you are",
  "responsibilities",
  "key responsibilities",
  "duties and responsibilities",
  "your responsibilities",
  "scope of work",
  "requirements",
  "minimum requirements",
  "qualifications",
  "minimum qualifications",
  "preferred qualifications",
  "skills",
  "experience",
  "nice to have",
  "benefits",
  "perks",
  "what we offer",
  "we offer",
  "why join us",
  "عن الدور",
  "عن الوظيفة",
  "عن الشركة",
  "المسؤوليات",
  "المسؤوليات الرئيسية",
  "المهام",
  "نطاق العمل",
  "المتطلبات",
  "المؤهلات",
  "المهارات",
  "الخبرة",
  "المزايا",
  "لماذا تنضم إلينا",
  "من نبحث عنه",
  "ما ستقوم به",
]);

function stripHeadingPunctuation(value: string) {
  return value.trim().replace(/\s*[:：]\s*$/u, "");
}

function isJobSectionHeading(value: string) {
  const heading = stripHeadingPunctuation(value)
    .replace(/^\*\*(.*?)\*\*$/u, "$1")
    .trim();

  if (!heading || heading.length > 80) {
    return false;
  }

  if (JOB_SECTION_HEADINGS.has(heading.toLowerCase())) {
    return true;
  }

  // Company introductions often use headings such as "About Janus Digital".
  // Keep the match deliberately short and sentence-free so ordinary prose that
  // happens to start with "About" is never promoted to a heading.
  return /^about\s+[^.!?؟]{1,60}$/iu.test(heading);
}

function asHeading(value: string) {
  // Job descriptions sit below the page's Description heading, so h4 keeps a
  // correct document hierarchy and a compact, reference-post-like visual size.
  return `#### ${stripHeadingPunctuation(value)}`;
}

function looksLikeSentenceListItem(value: string) {
  const text = value.trim();
  if (text.length >= 40) {
    return true;
  }

  return text.split(/\s+/u).filter(Boolean).length >= 7;
}

/**
 * Recover presentation structure from catalog descriptions without rewriting
 * their wording.
 *
 * ATS and Google Jobs payloads commonly flatten HTML list items to Unicode
 * bullets. Markdown renderers then collapse those lines into one paragraph,
 * which is how section headings and bullets ended up inline in issue #323.
 * This normalizer is intentionally conservative:
 * - existing Markdown remains Markdown;
 * - strong Unicode bullets become Markdown list items;
 * - a middle dot becomes a list delimiter only when the same line contains a
 *   sequence of sentence-length items (so "Python · TypeScript · AWS" stays
 *   untouched);
 * - only a bounded job-description heading vocabulary is promoted.
 */
export function normalizeJobDescriptionMarkdown(value: string) {
  const source = value.replace(/\r\n?/g, "\n").replace(/\u00a0/g, " ").trim();
  if (!source) {
    return "";
  }

  const expandedLines: string[] = [];
  let insideCodeFence = false;

  for (const originalLine of source.split("\n")) {
    const trimmed = originalLine.trim();

    if (/^(?:```|~~~)/u.test(trimmed)) {
      insideCodeFence = !insideCodeFence;
      expandedLines.push(originalLine);
      continue;
    }

    if (insideCodeFence || !trimmed) {
      expandedLines.push(originalLine);
      continue;
    }

    const middleDotParts = trimmed
      .split(MIDDLE_DOT_SEPARATOR)
      .map((part) => part.trim())
      .filter(Boolean);
    const middleDotItems = middleDotParts.slice(1);
    const isLinearizedMiddleDotList =
      middleDotParts.length >= 3 &&
      middleDotItems.filter(looksLikeSentenceListItem).length >= 2;

    if (isLinearizedMiddleDotList) {
      const [lead, ...items] = middleDotParts;
      if (lead) {
        expandedLines.push(isJobSectionHeading(lead) ? asHeading(lead) : lead);
        expandedLines.push("");
      }
      expandedLines.push(...items.map((item) => `- ${item}`));
      continue;
    }

    const withMarkdownBullets = originalLine.replace(
      STRONG_BULLET,
      (_match, prefix: string) => `${prefix ? "\n" : ""}- `,
    );
    const lineParts = withMarkdownBullets.split("\n");

    for (const [index, linePart] of lineParts.entries()) {
      const part = linePart.trim();
      if (!part) {
        continue;
      }

      if (index === 0 && isJobSectionHeading(part)) {
        expandedLines.push(asHeading(part));
      } else {
        expandedLines.push(part);
      }

      if (index === 0 && lineParts.length > 1) {
        expandedLines.push("");
      }
    }
  }

  const spacedLines: string[] = [];
  let previousKind: "blank" | "bullet" | "heading" | "text" = "blank";
  let insideSpacingCodeFence = false;

  for (const line of expandedLines) {
    const trimmed = line.trim();
    if (/^(?:```|~~~)/u.test(trimmed)) {
      spacedLines.push(line.trimEnd());
      insideSpacingCodeFence = !insideSpacingCodeFence;
      previousKind = "text";
      continue;
    }

    if (insideSpacingCodeFence) {
      spacedLines.push(line);
      continue;
    }

    const kind = !trimmed
      ? "blank"
      : /^#{1,6}\s+/u.test(trimmed)
        ? "heading"
        : /^(?:[-+*]|\d+[.)])\s+/u.test(trimmed)
          ? "bullet"
          : "text";

    const needsBlockBreak =
      kind !== "blank" &&
      previousKind !== "blank" &&
      (kind === "heading" ||
        previousKind === "heading" ||
        (kind === "bullet" && previousKind === "text") ||
        (kind === "text" && previousKind === "bullet"));

    if (needsBlockBreak) {
      spacedLines.push("");
    }

    spacedLines.push(trimmed ? line.trimEnd() : "");
    previousKind = kind;
  }

  return spacedLines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
